import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, Users, AlertCircle } from 'lucide-react';
import { joinCircle } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Landing page for shared circle-invite links. Auto-joins the caller into
 * the circle of the given `authorUserId` and then bounces them to
 * `/circles`. If the user is signed out we open the auth modal and wait
 * for them to authenticate before retrying.
 */
const JoinCircle = () => {
  const { authorUserId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, openAuthModal } = useAuth();
  const [state, setState] = useState('idle'); // idle | joining | error
  const [error, setError] = useState('');
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Prompt sign-in; the effect re-runs when `user` becomes truthy.
      openAuthModal();
      return;
    }
    if (attemptedRef.current) return;
    attemptedRef.current = true;
    setState('joining');
    joinCircle(authorUserId)
      .then(() => {
        // Small delay so users see the confirmation for a beat.
        setTimeout(() => navigate('/circles', { replace: true }), 400);
      })
      .catch((e) => {
        const msg =
          e?.response?.status === 400
            ? "You can't join your own circle."
            : e?.response?.status === 404
              ? "That circle doesn't exist anymore."
              : (e?.response?.data?.detail || 'Could not join the circle. Please try again.');
        setError(msg);
        setState('error');
      });
  }, [authorUserId, user, authLoading, openAuthModal, navigate]);

  return (
    <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="join-circle-page">
      <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-950/60 flex items-center justify-center text-amber-600 dark:text-amber-300 mb-4">
        <Users size={28} />
      </div>
      {authLoading || (!user && state === 'idle') ? (
        <>
          <h1 className="text-xl font-semibold mb-2">Sign in to join this circle</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Once you&apos;re signed in, we&apos;ll add this circle to your list.
          </p>
        </>
      ) : state === 'joining' ? (
        <>
          <h1 className="text-xl font-semibold mb-2 flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={18} /> Joining the circle…
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Just a moment while we add this circle to your list.
          </p>
        </>
      ) : state === 'error' ? (
        <>
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-950/60 flex items-center justify-center text-red-600 dark:text-red-300 mb-3">
            <AlertCircle size={22} />
          </div>
          <h1 className="text-xl font-semibold mb-2" data-testid="join-circle-error">Could not join</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/circles')}
            data-testid="join-circle-back-btn"
            className="h-10 px-5 rounded-full bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 text-sm font-medium"
          >
            Go to My Circles
          </button>
        </>
      ) : null}
    </div>
  );
};

export default JoinCircle;
